import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Row, Col, Card, Table, Button, Modal, Form, Badge, Alert, Spinner,
} from 'react-bootstrap';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  fetchFixedExpenses,
  createFixedExpense,
  updateFixedExpense,
  deleteFixedExpense,
  fetchFixedExpensePayments,
  payFixedExpense,
  clearLastExpense,
} from '../../redux/slices/fixedExpensesSlice';
import { fetchCategories } from '../../redux/slices/categoriesSlice';

const EMPTY_FORM = { name: '', description: '', amount: '', category_id: '' };
const EMPTY_PAY  = { amount: '', paid_date: format(new Date(), 'yyyy-MM-dd'), notes: '' };

const FixedExpenseList = () => {
  const dispatch = useDispatch();
  const { fixedExpenses, paymentsHistory, lastCreatedExpense, loading } =
    useSelector((s) => s.fixedExpenses);
  const { categories } = useSelector((s) => s.categories);

  // CRUD modal
  const [showModal, setShowModal]       = useState(false);
  const [editing, setEditing]           = useState(null);
  const [formData, setFormData]         = useState(EMPTY_FORM);

  // Pay modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingFE, setPayingFE]         = useState(null);
  const [payForm, setPayForm]           = useState(EMPTY_PAY);
  const [payLoading, setPayLoading]     = useState(false);

  // History modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyFE, setHistoryFE]               = useState(null);
  const [historyLoading, setHistoryLoading]     = useState(false);

  useEffect(() => {
    dispatch(fetchFixedExpenses());
    dispatch(fetchCategories());
  }, [dispatch]);

  // ── CRUD ────────────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (fe) => {
    setEditing(fe);
    setFormData({
      name: fe.name,
      description: fe.description || '',
      amount: fe.amount,
      category_id: fe.category?.id || '',
    });
    setShowModal(true);
  };

  const handleFormChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      amount: parseFloat(formData.amount),
      category_id: formData.category_id || null,
    };
    if (editing) {
      await dispatch(updateFixedExpense({ id: editing.id, data: payload }));
    } else {
      await dispatch(createFixedExpense(payload));
    }
    dispatch(fetchFixedExpenses());
    setShowModal(false);
  };

  const handleToggleActive = async (fe) => {
    await dispatch(updateFixedExpense({ id: fe.id, data: { is_active: !fe.is_active } }));
    dispatch(fetchFixedExpenses());
  };

  const handleDelete = async (fe) => {
    if (!window.confirm(`¿Eliminar "${fe.name}"? Esta acción no se puede deshacer.`)) return;
    await dispatch(deleteFixedExpense(fe.id));
    dispatch(fetchFixedExpenses());
  };

  // ── PAY ─────────────────────────────────────────────────────────────────────

  const openPayModal = (fe) => {
    setPayingFE(fe);
    setPayForm({ amount: fe.amount, paid_date: format(new Date(), 'yyyy-MM-dd'), notes: '' });
    dispatch(clearLastExpense());
    setShowPayModal(true);
  };

  const handlePayChange = (e) =>
    setPayForm({ ...payForm, [e.target.name]: e.target.value });

  const handlePay = async (e) => {
    e.preventDefault();
    setPayLoading(true);
    await dispatch(payFixedExpense({
      id: payingFE.id,
      data: { amount: parseFloat(payForm.amount), paid_date: payForm.paid_date, notes: payForm.notes || null },
    }));
    dispatch(fetchFixedExpenses());
    setPayLoading(false);
    // Keep modal open to show the success alert
  };

  const handleClosePayModal = () => {
    setShowPayModal(false);
    setPayingFE(null);
    dispatch(clearLastExpense());
  };

  // ── HISTORY ─────────────────────────────────────────────────────────────────

  const openHistory = async (fe) => {
    setHistoryFE(fe);
    setHistoryLoading(true);
    setShowHistoryModal(true);
    await dispatch(fetchFixedExpensePayments(fe.id));
    setHistoryLoading(false);
  };

  // ── STATS ───────────────────────────────────────────────────────────────────

  const activeCount   = fixedExpenses.filter(fe => fe.is_active).length;
  const inactiveCount = fixedExpenses.filter(fe => !fe.is_active).length;
  const monthlyTotal  = fixedExpenses
    .filter(fe => fe.is_active)
    .reduce((sum, fe) => sum + fe.amount, 0);

  if (loading) {
    return <Container className="mt-4"><div className="text-center">Cargando...</div></Container>;
  }

  return (
    <Container className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col><h2>Gastos Fijos</h2></Col>
        <Col className="text-end">
          <Button variant="primary" onClick={openCreate}>+ Nuevo Gasto Fijo</Button>
        </Col>
      </Row>

      {/* Summary */}
      <Row className="mb-4 g-3">
        <Col md={4}>
          <Card bg="primary" text="white">
            <Card.Body>
              <Card.Title>Total Mensual Activo</Card.Title>
              <h3>${monthlyTotal.toFixed(2)}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card bg="success" text="white">
            <Card.Body>
              <Card.Title>Activos</Card.Title>
              <h3>{activeCount}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card bg="secondary" text="white">
            <Card.Body>
              <Card.Title>Inactivos</Card.Title>
              <h3>{inactiveCount}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card>
        <Card.Body>
          {fixedExpenses.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No tienes gastos fijos registrados</p>
              <Button variant="primary" onClick={openCreate}>Crear tu primer gasto fijo</Button>
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Categoría</th>
                  <th>Monto</th>
                  <th>Último Pago</th>
                  <th>Pagos</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {fixedExpenses.map((fe) => (
                  <tr key={fe.id} style={{ opacity: fe.is_active ? 1 : 0.55 }}>
                    <td>
                      <strong>{fe.name}</strong>
                      {fe.description && (
                        <div><small className="text-muted">{fe.description}</small></div>
                      )}
                    </td>
                    <td>
                      {fe.category ? (
                        <Badge style={{ backgroundColor: fe.category.color }}>{fe.category.name}</Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-danger fw-bold">${fe.amount.toFixed(2)}</td>
                    <td>
                      {fe.last_payment_date
                        ? format(new Date(fe.last_payment_date + 'T00:00:00'), 'dd/MM/yyyy', { locale: es })
                        : <span className="text-muted">Sin pagos</span>
                      }
                    </td>
                    <td className="text-center">{fe.payments_count}</td>
                    <td>
                      <Badge bg={fe.is_active ? 'success' : 'secondary'}>
                        {fe.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        {fe.is_active && (
                          <Button variant="success" size="sm" onClick={() => openPayModal(fe)}>
                            Pagar
                          </Button>
                        )}
                        <Button variant="outline-info" size="sm" onClick={() => openHistory(fe)}>
                          Historial
                        </Button>
                        <Button variant="outline-warning" size="sm" onClick={() => openEdit(fe)}>
                          Editar
                        </Button>
                        <Button
                          variant={fe.is_active ? 'outline-secondary' : 'outline-success'}
                          size="sm"
                          onClick={() => handleToggleActive(fe)}
                        >
                          {fe.is_active ? 'Desactivar' : 'Activar'}
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(fe)}>
                          Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* ── CRUD Modal ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Editar Gasto Fijo' : 'Nuevo Gasto Fijo'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text" name="name" value={formData.name}
                onChange={handleFormChange} placeholder="Ej: Alquiler, Internet, Gym" required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción (opcional)</Form.Label>
              <Form.Control
                type="text" name="description" value={formData.description}
                onChange={handleFormChange} placeholder="Descripción adicional"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Monto</Form.Label>
              <Form.Control
                type="number" step="0.01" name="amount" value={formData.amount}
                onChange={handleFormChange} placeholder="0.00" required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Categoría (opcional)</Form.Label>
              <Form.Select name="category_id" value={formData.category_id} onChange={handleFormChange}>
                <option value="">Sin categoría</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button variant="primary" type="submit">{editing ? 'Actualizar' : 'Crear'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Pay Modal ── */}
      <Modal show={showPayModal} onHide={handleClosePayModal}>
        <Modal.Header closeButton>
          <Modal.Title>Registrar Pago — {payingFE?.name}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePay}>
          <Modal.Body>
            {lastCreatedExpense ? (
              <Alert variant="success">
                <Alert.Heading>✓ Pago registrado</Alert.Heading>
                <p className="mb-0">
                  Se creó el egreso <strong>"{lastCreatedExpense.description}"</strong> por{' '}
                  <strong>${lastCreatedExpense.amount?.toFixed(2)}</strong> en la sección de Egresos.
                </p>
              </Alert>
            ) : (
              <>
                <Alert variant="info" className="mb-3">
                  Al registrar el pago se creará automáticamente un <strong>egreso</strong> en tu historial.
                </Alert>
                <Form.Group className="mb-3">
                  <Form.Label>Monto a pagar</Form.Label>
                  <Form.Control
                    type="number" step="0.01" name="amount" value={payForm.amount}
                    onChange={handlePayChange} required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de pago</Form.Label>
                  <Form.Control
                    type="date" name="paid_date" value={payForm.paid_date}
                    onChange={handlePayChange} required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Notas (opcional)</Form.Label>
                  <Form.Control
                    type="text" name="notes" value={payForm.notes}
                    onChange={handlePayChange} placeholder="Referencia, comprobante, etc."
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClosePayModal}>
              {lastCreatedExpense ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!lastCreatedExpense && (
              <Button variant="success" type="submit" disabled={payLoading}>
                {payLoading ? <Spinner size="sm" animation="border" /> : 'Registrar Pago'}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── History Modal ── */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Historial de Pagos — {historyFE?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyLoading ? (
            <div className="text-center py-3"><Spinner animation="border" /></div>
          ) : (paymentsHistory[historyFE?.id] || []).length === 0 ? (
            <p className="text-center text-muted">Aún no hay pagos registrados para este gasto.</p>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Monto</th>
                  <th>Notas</th>
                  <th>Egreso #</th>
                </tr>
              </thead>
              <tbody>
                {(paymentsHistory[historyFE?.id] || []).map((p) => (
                  <tr key={p.id}>
                    <td>{format(new Date(p.paid_date + 'T00:00:00'), 'dd/MM/yyyy', { locale: es })}</td>
                    <td className="text-danger">${p.amount.toFixed(2)}</td>
                    <td>{p.notes || <span className="text-muted">—</span>}</td>
                    <td className="text-muted">{p.expense_id ? `#${p.expense_id}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FixedExpenseList;
